import type { RefObject } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import { REMINDER_DUPLICATE_MS } from './calendarReminders';

/**
 * The browser half every in-app reminder shares — calendar
 * (useCalendarReminders.ts) and tasks (useTaskReminders.ts): finding the
 * service worker, asking whether the push already put this notice on screen,
 * and raising it in a way that works on Android too. What differs between the
 * two — what is due, who says yes, what the notice says — stays in each hook.
 */

export const NOTICE_ICON = '/icons/icon-192.png';
export const NOTICE_BADGE = '/icons/badge-96.png';

/**
 * The key the Background notifications card reads GET /push/background under
 * (routes/settings/NotificationsSettings.tsx), so every reader shares one answer.
 */
export const BACKGROUND_PUSH_QUERY = ['push', 'background'] as const;

export function notificationsSupported(): boolean {
  return typeof Notification !== 'undefined';
}

export async function serviceWorkerRegistration(): Promise<ServiceWorkerRegistration | undefined> {
  try {
    return await navigator.serviceWorker?.getRegistration();
  } catch {
    return undefined;
  }
}

/** Whether this browser holds a push subscription the server can reach. */
export async function hasPushSubscription(
  registration: ServiceWorkerRegistration | undefined,
): Promise<boolean> {
  try {
    return (await registration?.pushManager?.getSubscription()) != null;
  } catch {
    return false;
  }
}

/** Whether this reminder is already on screen — the push got here first. */
export async function alreadyShown(
  registration: ServiceWorkerRegistration | undefined,
  tag: string,
  now: number,
): Promise<boolean> {
  try {
    const shown = (await registration?.getNotifications({ tag })) ?? [];
    return shown.some((notice) => {
      const at = (notice.data as { shownAt?: unknown } | null | undefined)?.shownAt;
      return typeof at !== 'number' || now - at < REMINDER_DUPLICATE_MS;
    });
  } catch {
    return false;
  }
}

export interface ReminderNotice {
  title: string;
  body: string;
  tag: string;
  /** Opened in-app on click; the service worker opens it on Android. */
  url: string;
  now: number;
}

/**
 * Raises one reminder notice. A click brings the window forward and opens
 * `url` in-app, without a reload.
 */
export async function raiseReminderNotice(
  notice: ReminderNotice,
  registration: ServiceWorkerRegistration | undefined,
  navigate: RefObject<NavigateFunction>,
): Promise<void> {
  const { title, body, tag, url, now } = notice;
  const data = { url, shownAt: now };
  try {
    const notification = new Notification(title, {
      body,
      tag,
      icon: NOTICE_ICON,
      badge: NOTICE_BADGE,
      data,
    });
    notification.onclick = () => {
      window.focus();
      notification.close();
      navigate.current(url);
    };
    return;
  } catch {
    /* Android: see below. */
  }
  // Chrome on Android refuses `new Notification()` ("Illegal constructor");
  // there only the service worker may raise a notice, and its
  // `notificationclick` opens `data.url` (public/push-sw.js).
  try {
    await registration?.showNotification(title, {
      body,
      tag,
      icon: NOTICE_ICON,
      badge: NOTICE_BADGE,
      renotify: true,
      data,
    } as NotificationOptions);
  } catch {
    /* A browser that refuses both is not worth crashing over. */
  }
}
